import styled, { css } from 'styled-components';
import { forwardRef, useMemo, type CSSProperties, type ReactNode } from 'react';
import type { Breakpoint, CompiledCell } from '@packages/matrix-solver';
import { rem } from '@packages/ui/tokens';
import { palette } from '@packages/ui/tokens';
import { Lozenge } from '@packages/ui/primitives';
import { hasVisibleSectionTitle } from '../../utils/sectionDividerTitle';

export const MATRIX_FIELD_COLUMN_GAP = '16px';
export const MATRIX_FIELD_ROW_GAP = '18px';

function buildItemPlacementCSS(cells: Partial<Record<Breakpoint, CompiledCell>>): string {
  const cell = cells.base;
  if (!cell) return '';

  let css = `grid-column: ${cell.gridColumnStart} / span ${cell.gridSpan}; grid-row: ${cell.gridRowStart};`;

  if (cell.minWidthPx != null || cell.maxWidthPx != null) {
    css += ' width: 100%; margin-right: auto;';
    if (cell.minWidthPx != null) css += ` min-width: ${cell.minWidthPx}px;`;
    if (cell.maxWidthPx != null) css += ` max-width: ${cell.maxWidthPx}px;`;
  }

  return css;
}

const MatrixFieldsRootBase = styled.div`
  box-sizing: border-box;
  display: grid;
  width: 100%;
  column-gap: ${MATRIX_FIELD_COLUMN_GAP};
  row-gap: var(--matrix-row-gap, ${MATRIX_FIELD_ROW_GAP});
  grid-auto-rows: auto;
  align-items: start;

  /*
   * Section-lead spacing: breathe from the prior sibling block (fields / each / udf).
   * Skip when this grid is the first child — otherwise the first section creates a
   * dead 32px gap under the page title or card chrome (e.g. Participant #1 badge).
   */
  &[data-section-lead]:not(:first-child) {
    margin-top: ${rem(32)};
  }

  &[data-standalone-header] {
    margin-bottom: ${rem(6)};
  }

  &[data-standalone-header]:not(:first-child) {
    margin-top: ${rem(32)};
  }
`;

export const MatrixFieldsRoot = forwardRef<
  HTMLDivElement,
  {
    columnCount: number;
    children?: ReactNode;
    style?: CSSProperties;
    /** Block starts with a section-divider — apply lead margin when not first sibling. */
    sectionLead?: boolean;
    /** Divider-only header block (e.g. each-node title) — also keeps bottom clearance. */
    standaloneHeader?: boolean;
  }
>(function MatrixFieldsRoot(
  { columnCount, children, style: extraStyle, sectionLead, standaloneHeader },
  ref,
) {
  const style = useMemo<CSSProperties>(
    () => ({ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`, ...extraStyle }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnCount, extraStyle],
  );
  return (
    <MatrixFieldsRootBase
      ref={ref}
      style={style}
      data-section-lead={sectionLead || undefined}
      data-standalone-header={standaloneHeader || undefined}
    >
      {children}
    </MatrixFieldsRootBase>
  );
});

const TRANSIENT = ['$placementCSS', '$isVirtual'] as const;
const shouldForward = (prop: string | number): boolean =>
  !TRANSIENT.includes(prop as (typeof TRANSIENT)[number]);

export const MatrixFieldShell = styled.div.withConfig({ shouldForwardProp: shouldForward })<{
  $placementCSS: string;
  $isVirtual: boolean;
}>`
  box-sizing: border-box;
  min-width: 0;

  ${({ $placementCSS }) => $placementCSS}

  ${({ $isVirtual }) =>
    $isVirtual &&
    css`
      visibility: hidden;
      pointer-events: none;
      height: 0;
      min-height: 0;
      margin: 0;
      padding: 0;
      overflow: hidden;
      opacity: 0;
    `}
`;

export function compileCellPlacement(
  cellMap: Record<string, Partial<Record<Breakpoint, CompiledCell>>>,
  id: string
): string {
  return buildItemPlacementCSS(cellMap[id] ?? {});
}

/**
 * Section divider spacing rationale (legacy parity):
 *
 * Text/heading dividers (participant form address sections):
 * - margin-bottom: -6px counteracts row-gap(18) → ~12px net to first field
 *   (legacy `.employeeAddress` padding-bottom: 12px)
 * - margin-top: 20px on non-first dividers (row-gap + extra rhythm)
 *
 * Lozenge dividers (performance rule Tag sections, plan-management):
 * - margin-top: 0 on all lozenges — matrix row-gap already adds 18px after the
 *   prior field row; extra margin-top stacked ~38px before 2nd lozenge vs legacy
 *   Tag with no wrapper margin between sections.
 * - margin-bottom: -2px counteracts row-gap → ~16px net ≈ legacy Tag + mt-4 (16px)
 */
export const TEXT_SECTION_DIVIDER_MARGIN_TOP_NON_FIRST = rem(20);
export const LOZENGE_SECTION_DIVIDER_MARGIN_TOP = '0';
export const COMPACT_SECTION_DIVIDER_MARGIN_TOP = rem(8);
export const TEXT_SECTION_DIVIDER_MARGIN_BOTTOM = rem(-6);
/** row-gap(18) - 2 = 16px net — legacy Tag section uses mt-4 (16px) to fields */
export const LOZENGE_SECTION_DIVIDER_MARGIN_BOTTOM = rem(-2);
/** row-gap(18) - 2 = 16px net — legacy h5 subheading uses mb-4 (16px) to next row */
export const COMPACT_SECTION_DIVIDER_MARGIN_BOTTOM = rem(-2);

const SectionDividerRoot = styled.div<{
  $placementCSS: string;
  $hasDivider: boolean;
  $isFirst: boolean;
  $isLozenge: boolean;
  $titleSpacing: 'default' | 'compact';
}>`
  box-sizing: border-box;
  min-width: 0;
  margin-top: ${({ $isFirst, $isLozenge, $titleSpacing }) => {
    if ($isFirst || $isLozenge) return '0';
    if ($titleSpacing === 'compact') return COMPACT_SECTION_DIVIDER_MARGIN_TOP;
    return TEXT_SECTION_DIVIDER_MARGIN_TOP_NON_FIRST;
  }};
  margin-bottom: ${({ $isLozenge, $titleSpacing }) => {
    if ($isLozenge) return LOZENGE_SECTION_DIVIDER_MARGIN_BOTTOM;
    if ($titleSpacing === 'compact') return COMPACT_SECTION_DIVIDER_MARGIN_BOTTOM;
    return TEXT_SECTION_DIVIDER_MARGIN_BOTTOM;
  }};
  ${({ $placementCSS }) => $placementCSS}

  ${({ $hasDivider }) =>
    $hasDivider &&
    css`
      padding-top: ${rem(8)};
      border-top: ${rem(1)} solid var(--ui-border-muted, ${palette.CulturedWhite});
    `}
`;

const DividerHeading = styled.div`
  font-family: var(--ui-font-heading, var(--ui-font-sans, 'HK Grotesk', sans-serif));
  font-size: ${rem(20)};
  font-weight: 400;
  line-height: ${rem(24)};
  color: #595959;
  margin-bottom: 0;
`;

const DividerLabel = styled.div`
  font-family: var(--ui-font-heading, var(--ui-font-sans, 'HK Grotesk', sans-serif));
  font-size: ${rem(12)};
  font-weight: 500;
  line-height: ${rem(18)};
  color: var(--ui-text-muted, ${palette.SpanishGray});
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: ${rem(4)};
`;

const DividerSubtitle = styled.p`
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(20)};
  color: var(--ui-text-muted, ${palette.SpanishGray});
  margin: ${rem(-8)} 0 ${rem(16)} 0;
`;

export interface MatrixSectionDividerProps {
  /** Section heading. When absent or whitespace-only, renders border-only (if `divider`) or silent row-break. */
  title?: string;
  subtitle?: string;
  titleVariant?: 'label' | 'heading';
  divider?: boolean;
  semanticId?: string;
  placementCSS: string;
  /** True when this is the first section divider in the layout — suppresses top margin. */
  isFirst?: boolean;
  /** Render title as a Lozenge badge instead of plain text. */
  titleStyle?: 'text' | 'lozenge' | 'row-label';
  /** Lozenge variant when `titleStyle` is `'lozenge'`. */
  lozengeVariant?: string;
  /** Tighter vertical rhythm for in-form subheadings (legacy `mt-2 mb-4`). */
  titleSpacing?: 'default' | 'compact';
}

export function MatrixSectionDivider({
  title,
  subtitle,
  titleVariant = 'label',
  divider = true,
  semanticId,
  placementCSS,
  isFirst = false,
  titleStyle = 'text',
  lozengeVariant = 'success',
  titleSpacing = 'default',
}: MatrixSectionDividerProps): React.ReactElement {
  const showTitle = hasVisibleSectionTitle(title);

  if (!showTitle && !divider) {
    return <div style={{ gridColumn: '1 / -1', height: 0 }} aria-hidden='true' />;
  }

  if (!showTitle && divider) {
    return (
      <SectionDividerRoot
        $placementCSS={placementCSS}
        $hasDivider
        $isFirst={isFirst}
        $isLozenge={false}
        $titleSpacing={titleSpacing}
        data-semantic-id={semanticId}
        aria-hidden='true'
      />
    );
  }

  const TitleComponent = titleVariant === 'heading' ? DividerHeading : DividerLabel;

  return (
    <SectionDividerRoot
      $placementCSS={placementCSS}
      $hasDivider={divider}
      $isFirst={isFirst}
      $isLozenge={titleStyle === 'lozenge'}
      $titleSpacing={titleSpacing}
      data-semantic-id={semanticId}>
      {titleStyle === 'lozenge' ? (
        <Lozenge variant={lozengeVariant as 'success' | 'info' | 'warning' | 'default'} title={title} />
      ) : (
        <TitleComponent>{title}</TitleComponent>
      )}
      {subtitle != null && subtitle !== '' && <DividerSubtitle>{subtitle}</DividerSubtitle>}
    </SectionDividerRoot>
  );
}
